import { useQuery } from '@tanstack/react-query'
import ky from 'ky'

import { extractHttpError } from '../../../components/api/extractHttpError'
import { API_PREFIX } from '../../../config'
import { logEvents } from '../../logEvents'

export type VoiceMailPayload = {
  phoneNumber: string
}

// Unified response type for both APIs based on actual schema
export type VoiceMailResponse = {
  version: number
  uid: string
  sessionId: string
  organizationUid: string
  created: string
  phoneNumber: string
  status: 'Processing' | 'Error' | 'Ok'
  date: string | null
  transcription: string | null
  summary: string | null
  topic: string | null
  subTopic: string | null
  question: string | null
  recommendedAnswer: RecommendedAnswer | null
}

export type RecommendedAnswerLink = {
  id: number
  title: string
  url: string
  score: number
  language: string
  puzzelKbId: string | null
}

export type RecommendedAnswer = {
  queryId: string
  question: string
  date: string
  reply: string | null
  links: RecommendedAnswerLink[]
  errorCode: string | null
  errorMessage: string | null
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

export const useVoiceMails = (
  copilotToken: string | null,
  phoneNumber: string,
) => {
  const enabled = !!(copilotToken && phoneNumber)
  return useQuery<VoiceMailResponse | null>({
    enabled,
    // Once we have a uid, never refetch this again for this phoneNumber/token
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    queryKey: ['voice-mails', 'uid', copilotToken, phoneNumber],
    refetchInterval: (d) => {
      if (d.state.dataUpdateCount > 60) return false
      // Poll while uid is not yet available
      if (d.state.data?.status === 'Processing') return 1000
    },
    queryFn: async ({ signal }) => {
      let count = 0
      while (count < 60) {
        try {
          const response = await ky.get(
            `${API_PREFIX}/co-pilot/v1/voice-mails/by-phone-number`,
            {
              signal,
              searchParams: {
                phoneNumber,
                date: new Date().toJSON(),
                token: copilotToken || '',
              },
            },
          )

          if (response.status !== 204) {
            return await response.json<VoiceMailResponse>()
          }
        } catch (error) {
          // React Query cancels this poll on unmount; that is not a failure
          if (signal.aborted) throw error

          const e = await extractHttpError(error)
          logEvents({
            eventType: 'useVoiceMails error',
            level: 'error',
            name: e.name,
            message: e.message,
            ...e.cause,
          })

          throw e
        }

        await sleep(1000)
        count = count + 1
      }

      return null
    },
  })
}
